import type { Branding, LegendLabel } from '@better-lights/core';
import { useAccount } from '../account/store.ts';
import { api } from '../api/instance.ts';
import type { Catalog } from '../api/types.ts';
import { db } from '../storage/db.ts';

/**
 * Catalog documents: the personal one (`/v1/me/catalog`: settings, price list, branding) and org
 * ones (`/v1/orgs/{id}/catalog`: shared price list + company branding). Cached in IndexedDB so
 * quotes work offline; saved with `baseRev` (the server merges like projects).
 */
export interface CatalogDoc {
  legendLabels?: Record<string, LegendLabel>;
  currency?: string;
  branding?: Branding;
  [key: string]: unknown;
}

export interface CachedCatalog {
  rev: number;
  doc: CatalogDoc;
}

function isPersonal(scope: string): boolean {
  const me = useAccount.getState().me;
  return !me || scope === me.personalScope;
}

function key(scope: string): string {
  return isPersonal(scope) ? `catalog.me.${scope}` : `catalog.${scope}`;
}

function fromApi(c: Catalog): CachedCatalog {
  return { rev: c.rev, doc: (c.doc ?? {}) as CatalogDoc };
}

export async function cachedCatalog(scope: string): Promise<CachedCatalog | null> {
  const v = (await db().kv.get(key(scope)))?.value as Catalog | CachedCatalog | undefined;
  if (!v) return null;
  return 'orgId' in v ? fromApi(v as Catalog) : (v as CachedCatalog);
}

/** Latest catalog (network when signed in, else the cache). */
export async function loadCatalog(scope: string): Promise<CachedCatalog | null> {
  if (!useAccount.getState().session) return cachedCatalog(scope);
  try {
    const c = fromApi(isPersonal(scope) ? await api.meCatalog() : await api.catalog(scope));
    await db().kv.put({ key: key(scope), value: c });
    return c;
  } catch {
    return cachedCatalog(scope);
  }
}

/** Saves a patch of the catalog document (merged by the server against `baseRev`). */
export async function saveCatalog(scope: string, patch: Partial<CatalogDoc>): Promise<CachedCatalog> {
  const cur = (await loadCatalog(scope)) ?? { rev: 0, doc: {} };
  const doc = { ...cur.doc, ...patch };
  const saved = fromApi(
    isPersonal(scope) ? await api.putMeCatalog(cur.rev, doc) : await api.putCatalog(scope, cur.rev, doc),
  );
  await db().kv.put({ key: key(scope), value: saved });
  return saved;
}

export function brandingOf(c: CachedCatalog | null): Branding | null {
  const b = c?.doc.branding;
  return b && typeof b === 'object' ? b : null;
}
